import React, { useState, useEffect } from 'react';
import { supabase } from '../../../supabaseClient';
import { usePermissions } from '../../../utils/permissions';
import { HexColorPicker } from 'react-colorful';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Box, Typography, Card, CardContent, IconButton, TextField,
  Alert, Snackbar, useTheme, alpha, Popover, Grid
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Business as BusinessIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

// GlowWork styled components
const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: 16,
    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
    boxShadow: `0 24px 48px ${alpha(theme.palette.grey[900], 0.2)}`,
    overflow: 'hidden',
    background: 'white'
  }
}));

const StyledCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  borderRadius: 12,
  border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
  background: 'white',
  boxShadow: `0 4px 12px ${alpha(theme.palette.grey[400], 0.15)}`,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.2)}`
  }
}));

export default function DepartmentManager({ 
  open, 
  onClose, 
  companyId, 
  onDepartmentsChange 
}) {
  const theme = useTheme();
  const { canEditEmployees, canDeleteEmployees } = usePermissions();
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Form states
  const [editingDept, setEditingDept] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '', color: '#1976d2', showForm: false });
  
  // Delete confirmation states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [departmentToDelete, setDepartmentToDelete] = useState(null);
  
  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  
  // Color picker states
  const [colorPickerOpen, setColorPickerOpen] = useState(false);
  const [colorPickerAnchor, setColorPickerAnchor] = useState(null);

  useEffect(() => {
    if (open) {
      loadDepartments();
    }
  }, [open, companyId]);

  const loadDepartments = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('departments')
        .select('*')
        .eq('company_id', companyId)
        .eq('is_active', true)
        .order('name', { ascending: true });

      if (error) throw error;
      
      setDepartments(data || []);
    } catch (error) {
      console.error('Error loading departments:', error);
      setError('Error loading departments: ' + error.message);
    } finally {
      setLoading(false);
    }
  };


  const handleAddDepartment = () => {
    setEditingDept(null);
    setFormData({ 
      name: '', 
      description: '',
      color: '#1976d2',
      showForm: true
    });
  };

  const handleEditDepartment = (dept) => {
    setEditingDept(dept);
    setFormData({
      name: dept.name,
      description: dept.description || '',
      color: dept.color || '#1976d2'
    });
  };

  const showAlert = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleDeleteDepartment = (dept) => {
    setDepartmentToDelete(dept);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteDepartment = async () => {
    if (!departmentToDelete) return;

    try {
      const { error } = await supabase
        .from('departments')
        .delete()
        .eq('id', departmentToDelete.id);

      if (error) throw error;

      setDeleteDialogOpen(false);
      setDepartmentToDelete(null);
      showAlert('Department deleted successfully');
      loadDepartments();
      onDepartmentsChange();
    } catch (error) {
      console.error('Error deleting department:', error);
      showAlert('Error deleting department: ' + error.message, 'error');
    }
  };

  const cancelDeleteDepartment = () => {
    setDeleteDialogOpen(false);
    setDepartmentToDelete(null);
  };

  const checkDepartmentNameExists = (name) => {
    if (!name.trim()) return false;
    
    return departments.some(dept => 
      dept.name.toLowerCase() === name.trim().toLowerCase() && 
      (!editingDept || dept.id !== editingDept.id)
    );
  };

  const handleSaveDepartment = async () => {
    if (!formData.name.trim()) {
      setError('Department name is required');
      return;
    }

    if (checkDepartmentNameExists(formData.name)) {
      setError('Department name already exists');
      return;
    }

    try {
      const saveData = {
        company_id: companyId,
        name: formData.name.trim(),
        description: formData.description?.trim() || null,
        color: formData.color || '#1976d2'
      };

      if (editingDept) {
        // Update existing
        const { error } = await supabase
          .from('departments')
          .update(saveData)
          .eq('id', editingDept.id);

        if (error) throw error;
        showAlert('Department updated successfully');
      } else {
        // Create new
        const { error } = await supabase
          .from('departments')
          .insert(saveData);

        if (error) throw error;
        showAlert('Department created successfully');
      }

      setEditingDept(null);
      setFormData({ name: '', description: '', color: '#1976d2', showForm: false });
      loadDepartments();
      onDepartmentsChange();
    } catch (error) {
      console.error('Error saving department:', error);
      showAlert('Error saving department: ' + error.message, 'error');
    }
  };


  // Color picker functions
  const handleColorChange = (newColor) => {
    setFormData(prev => ({ ...prev, color: newColor }));
  };

  const handleColorPickerOpen = (event) => {
    setColorPickerAnchor(event.currentTarget);
    setColorPickerOpen(true);
  };

  const handleColorPickerClose = () => {
    setColorPickerOpen(false);
    setColorPickerAnchor(null);
  };

  const handleColorSelect = (color) => {
    setFormData(prev => ({ ...prev, color }));
    handleColorPickerClose();
  };

  // Simple Department Item Component
  function DepartmentItem({ dept }) {
    return (
      <StyledCard sx={{ mb: 2 }}>
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Box display="flex" alignItems="center" gap={1}>
            <Box
              sx={{
                width: 24,
                height: 24,
                backgroundColor: dept.color || '#1976d2',
                borderRadius: 1,
                mr: 1,
                border: `2px solid ${alpha(dept.color || '#1976d2', 0.3)}`,
                boxShadow: `0 2px 4px ${alpha(dept.color || '#1976d2', 0.2)}`
              }}
            />
            
            <Box flexGrow={1}>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {dept.name}
              </Typography>
              {dept.description && (
                <Typography variant="caption" color="text.secondary">
                  {dept.description}
                </Typography>
              )}
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {canEditEmployees() && (
                <IconButton
                  size="small"
                  onClick={() => handleEditDepartment(dept)}
                  sx={{ ml: 1 }}
                >
                  <EditIcon />
                </IconButton>
              )}
              
              {canDeleteEmployees() && (
                <IconButton
                  size="small"
                  onClick={() => handleDeleteDepartment(dept)}
                  sx={{ ml: 1 }}
                >
                  <DeleteIcon />
                </IconButton>
              )}
            </Box>
          </Box>
        </CardContent>
      </StyledCard>
    );
  }



  return (
    <StyledDialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { minHeight: '70vh' }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={2}>
          <SettingsIcon color="primary" />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 500 }}>
              Manage Departments
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Create, edit, and organize department hierarchy
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Add Root Department Button */}
        {canEditEmployees() && (
          <Box sx={{ mb: 3 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleAddDepartment()}
              sx={{ 
                fontWeight: 500,
                borderRadius: 1,
                px: 3,
                py: 1.5,
                background: 'black',
                color: 'white',
                '&:hover': {
                  background: 'black',
                  transform: 'translateY(-1px)'
                }
              }}
            >
              Add Departments
            </Button>
          </Box>
        )}

        {/* Department Form */}
        {canEditEmployees() && (editingDept || formData.showForm) && (
          <StyledCard sx={{ 
            mb: 3, 
            background: 'white',
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`
          }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 500, mb: 2 }}>
                {editingDept ? 'Edit Department' : 'New Department'}
              </Typography>
              
              <Box display="flex" gap={2} mb={2}>
                <TextField
                  fullWidth
                  label="Department Name *"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                />
                <TextField
                  fullWidth
                  label="Description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                />
              </Box>
              
              <Box display="flex" gap={2} mb={2} alignItems="center">
                <Typography variant="body2" sx={{ fontWeight: 500, minWidth: 100 }}>
                  Color:
                </Typography>
                
                {/* Current Color Display */}
                <Box
                  onClick={handleColorPickerOpen}
                  sx={{
                    width: 48,
                    height: 48,
                    backgroundColor: formData.color,
                    borderRadius: 2,
                    cursor: 'pointer',
                    border: `3px solid ${alpha(formData.color, 0.3)}`,
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: `0 4px 12px ${alpha(formData.color, 0.3)}`,
                    '&:hover': {
                      transform: 'scale(1.05)',
                      boxShadow: `0 6px 16px ${alpha(formData.color, 0.4)}`
                    }
                  }}
                >
                  <Typography variant="caption" sx={{ 
                    color: 'white', 
                    fontWeight: 500,
                    textShadow: '1px 1px 2px rgba(0,0,0,0.5)',
                    fontSize: '0.7rem'
                  }}>
                    PICK
                  </Typography>
                </Box>
                
                <TextField
                  label="Hex Color"
                  value={formData.color}
                  onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
                  placeholder="#1976d2"
                  sx={{ width: 120 }}
                  size="small"
                />
              </Box>
              
              {checkDepartmentNameExists(formData.name) && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Department name already exists
                </Alert>
              )}
              
              <Box display="flex" gap={2}>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveDepartment}
                  disabled={!formData.name.trim() || checkDepartmentNameExists(formData.name)}
                  sx={{ 
                    fontWeight: 500,
                    borderRadius: 1,
                    px: 3,
                    py: 1.5,
                    background: 'black',
                    color: 'white',
                    opacity: checkDepartmentNameExists(formData.name) ? 0.5 : 1,
                    '&:hover': {
                      background: 'black',
                      transform: 'translateY(-1px)'
                    }
                  }}
                >
                  {editingDept ? 'Update' : 'Create'} Department
                </Button>
                                  <Button
                    variant="outlined"
                    onClick={() => {
                      setEditingDept(null);
                      setFormData({ name: '', description: '', color: '#1976d2', showForm: false });
                    }}
                  >
                  Cancel
                </Button>
              </Box>
            </CardContent>
          </StyledCard>
        )}

        {/* Department List */}
        <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 2 }}>
          Departments
        </Typography>

        {loading ? (
          <Box textAlign="center" py={4}>
            <Typography>Loading departments...</Typography>
          </Box>
        ) : departments.length === 0 ? (
          <Box textAlign="center" py={4}>
            <BusinessIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              No Departments Yet
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Create your first department to get started
            </Typography>
          </Box>
        ) : (
          <Box>
            {departments.map(dept => (
              <DepartmentItem key={dept.id} dept={dept} />
            ))}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          Close
        </Button>
      </DialogActions>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={cancelDeleteDepartment}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 500 }}>
            Delete Department
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{departmentToDelete?.name}"? This action cannot be undone and will also remove all child departments.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDeleteDepartment}>
            Cancel
          </Button>
          <Button 
            onClick={confirmDeleteDepartment}
            color="error"
            variant="contained"
            sx={{ fontWeight: 500 }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Color Picker Popover */}
      <Popover
        open={colorPickerOpen}
        anchorEl={colorPickerAnchor}
        onClose={handleColorPickerClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            boxShadow: `0 8px 24px ${alpha(theme.palette.grey[900], 0.15)}`,
            p: 2,
            maxWidth: 320
          }
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 500, mb: 2, textAlign: 'center' }}>
          Choose Department Color
        </Typography>
        
        {/* Color Wheel */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          mb: 3,
          '& .react-colorful': {
            width: '200px !important',
            height: '200px !important'
          },
          '& .react-colorful__saturation': {
            borderRadius: '8px 8px 0 0'
          },
          '& .react-colorful__hue': {
            borderRadius: '0 0 8px 8px',
            height: '24px'
          },
          '& .react-colorful__pointer': {
            width: '20px',
            height: '20px',
            border: '3px solid #fff',
            boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
          }
        }}>
          <HexColorPicker 
            color={formData.color} 
            onChange={handleColorChange}
          />
        </Box>
        
        {/* Quick Preset Colors */}
        <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 1 }}>
          Popular Colors:
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          {['#1976d2', '#388e3c', '#f57c00', '#7b1fa2', '#c2185b', '#00796b', '#5d4037', '#e64a19', '#9c27b0', '#ff5722', '#607d8b', '#795548'].map(color => (
            <Box
              key={color}
              onClick={() => handleColorSelect(color)}
              sx={{
                width: 28,
                height: 28,
                backgroundColor: color,
                borderRadius: 1,
                cursor: 'pointer',
                border: formData.color === color ? '2px solid #000' : '1px solid rgba(0,0,0,0.1)',
                transition: 'all 0.2s ease',
                '&:hover': {
                  transform: 'scale(1.1)',
                  boxShadow: `0 2px 8px ${alpha(color, 0.4)}`
                }
              }}
            />
          ))}
        </Box>
        
        {/* Current Selection */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2, 
          p: 2, 
          borderRadius: 2, 
          backgroundColor: alpha(formData.color, 0.1),
          border: `1px solid ${alpha(formData.color, 0.3)}`
        }}>
          <Box
            sx={{
              width: 24,
              height: 24,
              backgroundColor: formData.color,
              borderRadius: 1,
              border: '1px solid rgba(0,0,0,0.1)'
            }}
          />
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            Selected: {formData.color}
          </Typography>
        </Box>
      </Popover>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      
    </StyledDialog>
  );
}